import { useCallback, useEffect, useRef, useState } from "react";
import {
  createFailoverState,
  failoverFailed,
  failoverStarted,
  failoverSucceeded,
  getSecondsLeft,
  OTP_FAILOVER_STATUSES,
} from "../../../utilities/otpFailover";

/**
 * Counts down the 2FA OTP timeout and, when it ends, asks for the code by voice call without any action
 * from the user.
 *
 * @param {object|null} delivery - `otp` of the latest send; a new object (resend) restarts the countdown
 * @param {() => Promise<object>} onFailover - resends the code by voice call, resolves with the new delivery
 */
export function useOtpFailover({ delivery, onFailover }) {
  const [state, setState] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  // Refs so the interval callback always sees the latest values and the failover can't fire twice
  const failedOverRef = useRef(false);
  const verifiedRef = useRef(false);
  const onFailoverRef = useRef(onFailover);

  useEffect(() => {
    onFailoverRef.current = onFailover;
  }, [onFailover]);

  useEffect(() => {
    failedOverRef.current = false;
    verifiedRef.current = false;
    setNow(Date.now());
    setState(delivery ? createFailoverState(delivery, Date.now()) : null);
  }, [delivery]);

  const triggerFailover = useCallback(() => {
    if (failedOverRef.current || verifiedRef.current) {
      return;
    }
    failedOverRef.current = true;
    setState((current) => current && failoverStarted(current));

    onFailoverRef
      .current()
      .then((fallbackDelivery) => {
        setState((current) =>
          current ? failoverSucceeded(current, fallbackDelivery) : current,
        );
      })
      .catch((error) => {
        setState((current) =>
          current ? failoverFailed(current, error.message) : current,
        );
      });
  }, []);

  const isCountingDown = state?.status === OTP_FAILOVER_STATUSES.countingDown;

  useEffect(() => {
    if (!isCountingDown || !state) {
      return undefined;
    }

    const interval = window.setInterval(() => {
      const current = Date.now();
      setNow(current);
      if (current >= state.deadline) {
        window.clearInterval(interval);
        triggerFailover();
      }
    }, 1000);

    return () => window.clearInterval(interval);
  }, [isCountingDown, state, triggerFailover]);

  /** Stops the countdown; returns how the code was delivered. */
  const markVerified = useCallback(() => {
    verifiedRef.current = true;
    setState(
      (current) =>
        current && { ...current, status: OTP_FAILOVER_STATUSES.settled },
    );

    return {
      channel: state ? state.activeChannel : null,
      usedFailover: Boolean(state?.usedFailover),
    };
  }, [state]);

  return {
    state,
    secondsLeft: state ? getSecondsLeft(state, now) : 0,
    markVerified,
  };
}
